import {
  Chat as ChatIcon,
  History as HistoryIcon,
  Menu as MenuIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material'
import { Box, IconButton, Tooltip } from '@mui/material'
import React from 'react'

interface BookEditorHeaderActionsProps {
  isAuthor: boolean
  isMobile: boolean
  section?: 'full' | 'primary' | 'menu'
  saving: boolean
  autoSaving: boolean
  hasUnsavedChanges: boolean
  menuButtonSx: Record<string, unknown>
  /**
   * 'fs' when the server is reading from disk (content session active);
   * 'db' when reading from Mongo. Author-only diagnostic chip.
   */
  contentSource?: 'fs' | 'db'
  onSave: () => void
  onOpenChapterList: () => void
  onOpenSearch: () => void
  onOpenBookSettings: () => void
  /** Opens the content-history view. Absent where the content repo is not on disk. */
  onOpenHistory?: () => void
  /** Open the assistant — reachable without a selection, for capture away from the desk. */
  onOpenAssistant?: () => void
}

export default function BookEditorHeaderActions({
  isAuthor,
  isMobile,
  section = 'full',
  saving,
  autoSaving,
  hasUnsavedChanges,
  contentSource,
  onSave,
  onOpenChapterList,
  onOpenSearch,
  onOpenBookSettings,
  onOpenHistory,
  onOpenAssistant,
}: BookEditorHeaderActionsProps) {
  if (!isAuthor) {
    return null
  }

  const saveIconTooltip = saving || autoSaving ? 'Saving...' : hasUnsavedChanges ? 'Save changes' : 'All changes saved'

  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

  const contentSourceBadge =
    isLocalhost && contentSource && (section === 'full' || section === 'primary') ? (
      <Tooltip
        title={
          contentSource === 'fs'
            ? 'Reading from disk (active content session). Changes go to files in manifesto-content/.'
            : 'Reading from Mongo (no active content session). Changes go to the database.'
        }
        arrow
      >
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'monospace',
            fontSize: '0.65rem',
            fontWeight: contentSource === 'fs' ? 700 : 500,
            lineHeight: 1,
            color: contentSource === 'fs' ? '#c2410c' : 'var(--text-secondary, #888)',
            backgroundColor: contentSource === 'fs' ? 'rgba(234, 88, 12, 0.12)' : 'transparent',
            border: '1px solid currentColor',
            borderRadius: '3px',
            px: 0.5,
            py: 0.25,
            mx: 0.5,
            opacity: contentSource === 'fs' ? 1 : 0.65,
            userSelect: 'none',
            flex: '0 0 auto',
          }}
          aria-label={`content source: ${contentSource}`}
        >
          {contentSource}
        </Box>
      </Tooltip>
    ) : null
  const mobileIconButtonSx = {
    color: 'var(--icon-default)',
    flex: '0 0 auto',
    p: 0.5,
    width: 30,
    height: 30,
  }
  const showPrimarySection = section === 'full' || section === 'primary'
  const showMenuSection = section === 'full' || section === 'menu'

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        minWidth: 0,
        flex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.125, md: 0.25 },
          minWidth: 0,
          flexWrap: 'nowrap',
          overflowX: { xs: 'auto', md: 'visible' },
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {contentSourceBadge}
        {isMobile && showPrimarySection && (
          <>
            {onOpenAssistant && (
              <IconButton
                size="small"
                onClick={onOpenAssistant}
                title="Ask the assistant"
                aria-label="Ask the assistant"
                sx={mobileIconButtonSx}
              >
                <ChatIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={onOpenSearch}
              title="Open search"
              aria-label="Open search"
              sx={mobileIconButtonSx}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </>
        )}
        {showPrimarySection && (
          <Tooltip title={saveIconTooltip}>
            <span>
              <IconButton
                size="small"
                onClick={() => onSave()}
                disabled={!hasUnsavedChanges || saving || autoSaving}
                aria-label="Save changes"
                sx={isMobile ? mobileIconButtonSx : { color: 'var(--icon-default)', mr: 0.25, flex: '0 0 auto' }}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {isMobile && showMenuSection && (
          <IconButton
            size="small"
            onClick={onOpenChapterList}
            title="Open chapter list"
            aria-label="Open chapter list"
            sx={mobileIconButtonSx}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        )}
        {!isMobile && (
          <>
            {onOpenAssistant && (
              <IconButton
                size="small"
                onClick={onOpenAssistant}
                title="Ask the assistant"
                aria-label="Ask the assistant"
                sx={{ color: 'var(--icon-default)', mr: 0.5 }}
              >
                <ChatIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={onOpenSearch}
              title="Open search"
              aria-label="Open search"
              sx={{ color: 'var(--icon-default)', mr: 0.5 }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
            {onOpenHistory && (
              <IconButton
                size="small"
                onClick={onOpenHistory}
                title="What changed"
                aria-label="What changed"
                sx={{ color: 'var(--icon-default)', mr: 0.5 }}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={onOpenBookSettings}
              title="Book settings"
              aria-label="Book settings"
              sx={{ color: 'var(--icon-default)', mr: 0.25 }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  )
}
